"use client";

import {
  isAdminAuthenticated as checkAdminAuth,
  onAuthStateChange,
} from "@/lib/admin-auth";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FaArrowRight } from "react-icons/fa";
import { FaPhone } from "react-icons/fa6";
import { GlassBar } from "./glass-bar";

const CALENDLY_URL =
  "https://calendly.com/webdifference/nouvelle-reunion?hide_gdpr_banner=1&background_color=282828&text_color=ffffff&primary_color=71ddae&month=2025-12";

const links = [
  { href: "/", label: "Accueil" },
  { href: "/offres", label: "Offres" },
  { href: "/projets", label: "Projets" },
  { href: "/guides", label: "Guides" },
];

const navLinkClass = (isActive: boolean) =>
  `relative text-sm font-medium transition-colors ${
    isActive ? "text-[#1C1C1C]" : "text-zinc-500 hover:text-[#1C1C1C]"
  }`;

export function SiteHeader() {
  const pathname = usePathname();
  const [isFloating, setIsFloating] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [isPhoneHovered, setIsPhoneHovered] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsFloating(window.scrollY > 32);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const phoneNumber = "06 38 72 30 38";

  useEffect(() => {
    setIsAdminAuthenticated(checkAdminAuth());
    const unsubscribe = onAuthStateChange((authenticated) => {
      setIsAdminAuthenticated(authenticated);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (menuOpen) {
      const scrollY = window.scrollY;

      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.width = "100%";

      return () => {
        document.body.style.overflow = "";
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.left = "";
        document.body.style.right = "";
        document.body.style.width = "";
        window.scrollTo(0, scrollY);
      };
    }
  }, [menuOpen]);

  return (
    <GlassBar position="top" isFloating={isFloating} className="w-full">
      <header
        className={`relative mx-auto grid w-full max-w-6xl grid-cols-[1fr_auto_1fr] items-center px-6 transition-all duration-500 ${
          isFloating ? "h-16" : "h-20"
        }`}
      >
        <button
          type="button"
          aria-label="Ouvrir le menu"
          aria-expanded={menuOpen}
          className="flex h-10 w-10 items-center justify-center rounded-full text-[#1C1C1C] transition hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-black/10 md:hidden"
          onClick={() => setMenuOpen((prev) => !prev)}
        >
          <span className="relative h-4 w-5">
            <span
              className={`absolute left-0 h-0.5 w-full rounded-full bg-current transition-all duration-300 ${
                menuOpen ? "top-1/2 rotate-45" : "top-0"
              }`}
            />
            <span
              className={`absolute left-0 top-1/2 h-0.5 w-full rounded-full bg-current transition-all duration-300 ${
                menuOpen ? "opacity-0" : "-translate-y-1/2"
              }`}
            />
            <span
              className={`absolute left-0 h-0.5 w-full rounded-full bg-current transition-all duration-300 ${
                menuOpen ? "top-1/2 -rotate-45" : "bottom-0"
              }`}
            />
          </span>
        </button>

        <Link href="/" className="flex items-center justify-self-start">
          <Image
            src="/assets/wd-vf.png"
            alt="Web Difference logo"
            width={1616}
            height={949}
            className={`h-auto transition-all duration-500 ${isFloating ? "w-[60px] md:w-[80px]" : "w-[68px] md:w-[92px]"}`}
            priority
          />
        </Link>

        <a
          href={CALENDLY_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Prendre rendez-vous"
          className={`inline-flex items-center justify-center justify-self-end rounded-full bg-[#1C1C1C] text-white shadow-[0_8px_24px_-10px_rgba(0,0,0,0.35)] transition-all duration-500 hover:bg-black md:hidden ${
            isFloating ? "h-10 w-10" : "h-11 w-11"
          }`}
          suppressHydrationWarning
        >
          <FaArrowRight className="-rotate-45 text-sm" aria-hidden="true" />
        </a>

        <nav className="hidden items-center justify-center gap-8 md:flex">
          {links.map((link) => {
            const isActive = mounted && pathname === link.href;
            return (
              <Link key={link.href} href={link.href} className={navLinkClass(isActive)}>
                {link.label}
              </Link>
            );
          })}
          {isAdminAuthenticated && (
            <Link
              href="/admin"
              className={navLinkClass(mounted && pathname === "/admin")}
            >
              Admin
            </Link>
          )}
        </nav>

        <div className="hidden items-center justify-self-end gap-2 md:flex">
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`group inline-flex items-center gap-2 rounded-full bg-[#1C1C1C] py-1.5 pl-4 font-semibold tracking-[0.01em] text-white shadow-[0_10px_28px_-12px_rgba(0,0,0,0.4)] transition-all duration-500 hover:bg-black ${
              isFloating ? "min-h-[40px] pr-1.5 text-sm" : "min-h-[44px] pr-2 text-sm"
            }`}
            suppressHydrationWarning
          >
            Prendre RDV
            <span
              className={`flex shrink-0 items-center justify-center rounded-full bg-white text-[#1C1C1C] transition group-hover:scale-105 ${
                isFloating ? "h-7 w-7" : "h-8 w-8"
              }`}
            >
              <FaArrowRight className="-rotate-45 text-xs" aria-hidden="true" />
            </span>
          </a>
          <Link
            href="tel:+33638723038"
            className="group relative inline-flex items-center justify-center overflow-hidden rounded-full border border-[#1C1C1C]/15 bg-[#1C1C1C] shadow-lg transition-all duration-500 hover:bg-[#1C1C1C]/90"
            style={{
              width: isPhoneHovered ? "200px" : isFloating ? "44px" : "48px",
              height: isFloating ? "44px" : "48px",
              gap: isPhoneHovered ? "0.5rem" : "0",
            }}
            aria-label="Appeler"
            onMouseEnter={() => setIsPhoneHovered(true)}
            onMouseLeave={() => setIsPhoneHovered(false)}
          >
            <FaPhone className="shrink-0 text-lg text-white" aria-hidden="true" />
            <span
              className="flex items-center whitespace-nowrap text-sm font-medium text-white transition-all duration-300 ease-out"
              style={{
                opacity: isPhoneHovered ? 1 : 0,
                transform: isPhoneHovered ? "translateX(0)" : "translateX(-10px)",
                width: isPhoneHovered ? "auto" : "0",
                overflow: "hidden",
              }}
            >
              {phoneNumber}
            </span>
          </Link>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[9999] relative flex flex-col bg-[#f3f3f3] text-[#1C1C1C] safe-top safe-bottom md:hidden"
            style={{
              height: "100dvh",
            }}
          >
            <div className="relative flex h-20 shrink-0 items-center justify-between border-b border-zinc-200/80 px-6 py-4">
              <Link href="/" className="flex items-center gap-3" onClick={() => setMenuOpen(false)}>
                <Image
                  src="/assets/wd-vf.png"
                  alt="Web Difference logo"
                  width={1616}
                  height={949}
                  className="h-auto w-[68px]"
                  priority
                />
              </Link>
              <button
                type="button"
                aria-label="Fermer le menu"
                onClick={() => setMenuOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-full text-[#1C1C1C] transition hover:bg-black/5"
              >
                <span className="relative h-4 w-5">
                  <span className="absolute left-0 top-1/2 h-0.5 w-full rotate-45 rounded-full bg-current" />
                  <span className="absolute left-0 top-1/2 h-0.5 w-full -rotate-45 rounded-full bg-current" />
                </span>
              </button>
            </div>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-6">
              <nav
                className="mb-10 flex flex-1 flex-col gap-2 overflow-y-auto overscroll-contain py-4"
                style={{ WebkitOverflowScrolling: "touch" }}
              >
                {links.map((link) => {
                  const isActive = pathname === link.href;
                  return (
                    <Link
                      key={link.href}
                      href={link.href}
                      onClick={() => setMenuOpen(false)}
                      className={`rounded-xl px-4 py-3 text-base font-semibold transition-all ${
                        isActive
                          ? "bg-[#1C1C1C] text-white shadow-sm"
                          : "text-zinc-700 hover:bg-black/5 hover:text-[#1C1C1C]"
                      }`}
                    >
                      {link.label}
                    </Link>
                  );
                })}
                {isAdminAuthenticated && (
                  <Link
                    href="/admin"
                    onClick={() => setMenuOpen(false)}
                    className={`rounded-xl px-4 py-3 text-base font-semibold transition-all ${
                      pathname === "/admin"
                        ? "bg-[#1C1C1C] text-white shadow-sm"
                        : "text-zinc-700 hover:bg-black/5 hover:text-[#1C1C1C]"
                    }`}
                  >
                    Admin
                  </Link>
                )}
              </nav>

              <div
                className="flex shrink-0 flex-col gap-3 border-t border-zinc-200/80 pt-2 pb-12"
                style={{ paddingBottom: "max(3rem, calc(env(safe-area-inset-bottom) + 1.5rem))" }}
              >
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMenuOpen(false)}
                  className="group inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full bg-[#1C1C1C] px-4 text-base font-semibold text-white shadow-[0_10px_28px_-12px_rgba(0,0,0,0.4)] transition hover:bg-black"
                  suppressHydrationWarning
                >
                  Prendre RDV
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-[#1C1C1C]">
                    <FaArrowRight className="-rotate-45 text-xs" aria-hidden="true" />
                  </span>
                </a>
                <Link
                  href="tel:+33638723038"
                  onClick={() => setMenuOpen(false)}
                  className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full border border-[#1C1C1C]/15 bg-[#1C1C1C]/5 px-4 text-base font-semibold text-[#1C1C1C] transition hover:bg-[#1C1C1C]/10"
                >
                  <FaPhone className="text-lg" aria-hidden="true" />
                  {phoneNumber}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </GlassBar>
  );
}
